import { Badge } from '@/design-system/badge';
import {
  Box,
  Flex,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { searchTabOptions } from '../contant';
import { useGetContactQuery } from '@/store/query/contacts';
import { useSelector } from 'react-redux';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import { useSearchOutreachQuery } from '@/store/query/outreaches';
import { useGetAllJobsQuery } from '@/store/query/ats-jobs';
import { Loader } from '../components/Loading';
import { OutreachList } from '../list/OutreachList';
import { ContactList } from '../list/ContactList';
import { EmptySearchResults } from '../components/EmptySearchResults';
import { useMixpanel } from '@/hooks/use-mixpanel';
import useRolePermissions from '@/hooks/use-role-permissions';
import { JobList } from '../list/JobList';
import { KText } from 'ui-library/components';
import { useKFlags } from '@/hooks/use-k-flags';

export const SearchTabs = ({
  searchTerm,
  handleSelect,
  setSelected,
  handleChangeSelected,
}) => {
  const [tabindex, setTabIndex] = useState(0);
  const currUser = useSelector(selectCurrUser);
  const { track } = useMixpanel();
  const { ats_enabled } = useKFlags();
  const { showAllSearch, showContacts, showOutreach, showJobs } =
    useRolePermissions();
  const ShowTab = {
    All: showAllSearch || ats_enabled,
    Contacts: showContacts || ats_enabled,
    Flow: showOutreach,
    Jobs: showJobs && ats_enabled,
  };

  const { data: contactData, isFetching: isContactFetching } =
    useGetContactQuery(
      {
        filters: {
          user: [{ id: 'all' }],
        },
        searchTerm,
        sort: {},
        page: 0,
        currUser,
        extendedField: false,
      },
      { skip: searchTerm.length <= 2 }
    );

  const { data: ats_jobs = [], isLoading: isJobsOptionsLoading } =
    useGetAllJobsQuery(
      {
        params: {
          view: 'card',
          sort_by: { created_at: 'DESC' },
          limit: 50,
          q: searchTerm,
        },
      },
      {
        skip: searchTerm.length <= 2,
      }
    );

  const { data: outreachData, isFetching: isOutreachFetching } =
    useSearchOutreachQuery({ searchTerm }, { skip: searchTerm.length <= 2 });

  //Handle arrow navigation on initial load and tab change
  useEffect(() => {
    if (!isContactFetching && !isOutreachFetching && !isJobsOptionsLoading) {
      setSelected(0);
      const items = document.querySelectorAll('.universal--search--item');
      items[0] && items[0].classList.add('active');
    }
  }, [isContactFetching, isOutreachFetching]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setSelected(0);
    const items = document.querySelectorAll('.universal--search--item');
    items[0] && items[0].classList.add('active');
  }, [tabindex]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isContactFetching || isOutreachFetching || isJobsOptionsLoading) {
    return <Loader />;
  }

  const contactCount = parseInt(contactData?.contact?.length) || 0;
  const outreachCount = parseInt(outreachData?.data?.outreaches?.length) || 0;
  const atsJobsCount = parseInt(ats_jobs?.length) || 0;

  return (
    <Tabs
      onChange={(tabindex) => {
        setTabIndex(tabindex);
        track({
          event: 'global-search-tab-change',
          prop: {
            'selected-tab': searchTabOptions.find(
              (serTab) => serTab.id === tabindex
            )?.label,
          },
        });
      }}
      isLazy
      onKeyDown={(e) => {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          e.stopPropagation();
          handleChangeSelected('down');
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          e.stopPropagation();
          handleChangeSelected('up');
        } else if (e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();
          handleSelect();
        }
      }}
    >
      <Flex alignItems="center" px="lg" mb="md">
        <TabList
          borderBottom="1px solid"
          borderColor="border-color"
          width="full"
        >
          {searchTabOptions.map(
            (option) =>
              ShowTab[option?.label] && (
                <Tab
                  key={option.id}
                  fontSize="sm"
                  _focus={{ outline: 'none' }}
                  p="4px 10px"
                  _selected={{
                    fontWeight: 700,
                    borderBottomColor: 'primary-500',
                  }}
                  mr="lg"
                >
                  <Badge
                    p="2px 6px"
                    background={
                      tabindex === option.id ? 'primary-100' : 'secondary-100'
                    }
                    color={
                      tabindex === option.id ? 'primary-500' : 'secondary-700'
                    }
                    minW="24px"
                    minH="20px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    borderRadius="xs"
                    mr="sm"
                  >
                    {option.label === 'All' &&
                      contactCount +
                        outreachCount +
                        (ats_enabled ? atsJobsCount : 0)}
                    {option.label === 'Flow' && outreachCount}
                    {option.label === 'Contacts' && contactCount}
                    {option.label === 'Jobs' && atsJobsCount}
                  </Badge>
                  <KText
                    as="span"
                    mr="6px"
                    color={
                      tabindex === option.id ? 'primary-500' : 'secondary-700'
                    }
                  >
                    {option.label}
                  </KText>
                </Tab>
              )
          )}
        </TabList>
      </Flex>
      <Box overflowY="auto" h="330px">
        <TabPanels>
          {(showAllSearch || ats_enabled) && (
            <TabPanel p="0">
              {!contactCount && !outreachCount && !atsJobsCount ? (
                <EmptySearchResults />
              ) : (
                <>
                  {contactCount > 0 && (
                    <ContactList
                      contactData={contactData}
                      searchTerm={searchTerm}
                    />
                  )}
                  {outreachCount > 0 && (
                    <OutreachList
                      outreachData={outreachData}
                      searchTerm={searchTerm}
                    />
                  )}
                  {atsJobsCount > 0 && ats_enabled && (
                    <JobList
                      jobData={ats_jobs}
                      searchTerm={searchTerm}
                      ats_enabled={ats_enabled}
                    />
                  )}
                </>
              )}
            </TabPanel>
          )}
          {(showContacts || ats_enabled) && (
            <TabPanel p="0">
              {contactCount > 0 ? (
                <ContactList
                  contactData={contactData}
                  searchTerm={searchTerm}
                />
              ) : (
                <EmptySearchResults />
              )}
            </TabPanel>
          )}
          {showOutreach && (
            <TabPanel p="0">
              {outreachCount > 0 ? (
                <OutreachList
                  outreachData={outreachData}
                  searchTerm={searchTerm}
                />
              ) : (
                <EmptySearchResults />
              )}
            </TabPanel>
          )}
          {showJobs && ats_enabled && (
            <TabPanel p="0">
              {atsJobsCount > 0 ? (
                <JobList jobData={ats_jobs} searchTerm={searchTerm} />
              ) : (
                <EmptySearchResults />
              )}
            </TabPanel>
          )}
        </TabPanels>
      </Box>
    </Tabs>
  );
};
